import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import API from '../../../services/API'
import TopPanel from './TopPanel'
import InternalNotes from './InternalNotes'
import TasksPane from './TasksPane'
import CentreTabs from './CentreTabs/CentreTabs'
import InvoicePanel from './Invoicing/InvoicePanel'
import DocumentsPanel from './Documents/DocumentsPanel'
import ParticipantsPanel from './Participants/ParticipantsPanel'
import ClientFeedbackPanel from './ClientFeedback/ClientFeedbackPanel'

//@MUI
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Slide from '@mui/material/Slide'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Save from '@mui/icons-material/Save'
import { Paper, Typography } from '@mui/material'
import { GlobalContext } from '../../../App'
import OpsPlan from './plan/OpsPlan/OpsPlan'
import RiskAssessment from './plan/RiskAssessment/RiskAssessment'
import MedPlan from './plan/ClinicalPlan/ClinicalPlan'
import DocumentGenerator from './DocumentGenerator/DocumentGenerator'
import BigModal from '../../../components/Modals/BigModal'
import AddNewEvent from '../AddNewEvent'
import VehiclesInvolved from './plan/OpsPlan/VehiclesInvolved'
import PEA from './PostEventAnalysis/PEA'
import PlanningIntelligence from './plan/PlanningIntelligence/PlanningIntelligence'
import ExemptionWizard from '../../registration/Assessment/ExemptionWizard'
import EventCosts from './plan/EventCosts/EventCosts'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

export const EventMetaContext = createContext({})
export const OpsPlanContext = createContext({})
export const MedPlanContext = createContext({})
export const PostEventAnalysisContext = createContext({})
export const RiskAssessmentContext = createContext({})
export const InvoiceContext = createContext({})
export const PlannerCommentsContext = createContext({})
export const ShiftContext = createContext({})
export const ChangePresentContext = createContext({})

const trackedEventFields = [
  'eventMeta',
  'invoice',
  'riskAssessment',
  'opsPlan',
  'medPlan',
  'postEventAnalysis',
]

const referenceFieldPaths = new Set([
  // Sent whole, not as dotted leaves — a partial GeoJSON point (coordinates
  // without `type`) is what dotted-path updates would otherwise produce.
  'eventAddress',
  'organisation',
  'mainContact',
  'EMPApprovedBy',
  'opsPlan.clinicalLead',
  'opsPlan.opsManager',
  'opsPlan.onsiteTeamLeader',
  'medPlan.nearestED',
  'medPlan.nearestTraumaUnit',
  'medPlan.nearestMTC',
  'medPlan.nearestPPCI',
  'medPlan.nearestStroke',
  'medPlan.nearestMIU',
  'medPlan.nearestPaediatrics',
  'medPlan.nearestPaediatricMTC',
])

const cloneData = (value) => JSON.parse(JSON.stringify(value ?? null))

const stableValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value._id) return value._id
    if (value.id) return value.id
  }
  return value
}

const valuesMatch = (a, b) =>
  JSON.stringify(stableValue(a) ?? null) ===
  JSON.stringify(stableValue(b) ?? null)

const shouldTreatAsLeaf = (value, prefix) =>
  value === null ||
  value === undefined ||
  Array.isArray(value) ||
  typeof value !== 'object' ||
  referenceFieldPaths.has(prefix)

const payloadValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value._id) return value._id
    if (value.id) return value.id
  }
  return value
}

const appendChangedFields = ({ payload, original, current, prefix }) => {
  if (
    shouldTreatAsLeaf(original, prefix) ||
    shouldTreatAsLeaf(current, prefix)
  ) {
    if (!valuesMatch(original, current)) {
      payload[prefix] = payloadValue(current)
    }
    return
  }

  const keys = new Set([
    ...Object.keys(original ?? {}),
    ...Object.keys(current ?? {}),
  ])

  keys.forEach((key) => {
    appendChangedFields({
      payload,
      original: original?.[key],
      current: current?.[key],
      prefix: `${prefix}.${key}`,
    })
  })
}

const SingleEvent = () => {
  const { id: eventID } = useParams()
  const [loading, setLoading] = useState(true)
  const [eventMeta, setEventMeta] = useState({})
  const [opsPlan, setOpsPlan] = useState({})
  const [medPlan, setMedPlan] = useState({})
  const [postEventAnalysis, setPostEventAnalysis] = useState({})
  const [riskAssessment, setRiskAssessment] = useState({})
  const [invoice, setInvoice] = useState({})
  const [shifts, setShifts] = useState([])
  const [plannerComments, setPlannerComments] = useState([])
  const [changesPresent, setChangesPresent] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [selectedTab, setSelectedTab] = useState('ops')
  const { setToast } = useContext(GlobalContext)
  const { setPageTitle } = useDocumentTitle()
  const [expandedDays, setExpandedDays] = useState([])
  const originalEventRef = useRef({})
  const changedFieldsRef = useRef(new Set())

  const markFieldChanged = (field) => {
    changedFieldsRef.current.add(field)
  }

  const updateTrackedState = (field, setter) => (value) => {
    markFieldChanged(field)
    setter(value)
  }

  const currentEventData = {
    eventMeta,
    invoice,
    riskAssessment,
    opsPlan,
    medPlan,
    postEventAnalysis,
  }

  const loadEvent = () => {
    setLoading(true)
    API.get('/event/' + eventID)
      .then((res) => {
        if (res.status === 200) {
          const {
            opsPlan,
            medPlan,
            postEventAnalysis,
            riskAssessment,
            invoice,
            shifts,
            plannerComments,
            ...meta
          } = res.data
          setEventMeta(meta)
          setOpsPlan(opsPlan ?? {})
          setMedPlan(medPlan ?? {})
          setPostEventAnalysis(postEventAnalysis ?? {})
          setRiskAssessment(riskAssessment ?? {})
          setInvoice(invoice ?? {})
          setShifts(shifts ?? [])
          setPlannerComments(plannerComments ?? [])
          originalEventRef.current = cloneData({
            eventMeta: meta,
            invoice: invoice ?? {},
            riskAssessment: riskAssessment ?? {},
            opsPlan: opsPlan ?? {},
            medPlan: medPlan ?? {},
            postEventAnalysis: postEventAnalysis ?? {},
          })
          changedFieldsRef.current.clear()
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setEventMeta({})
    loadEvent()
    return () => setEventMeta({})
  }, [eventID])

  const handleSave = () => {
    const payload = {
      eventID: eventMeta._id,
    }
    const changedFields =
      changedFieldsRef.current.size > 0
        ? Array.from(changedFieldsRef.current)
        : trackedEventFields

    changedFields.forEach((field) => {
      appendChangedFields({
        payload,
        original: originalEventRef.current?.[field],
        current: currentEventData[field],
        prefix: field === 'eventMeta' ? '' : field,
      })
    })

    Object.keys(payload).forEach((key) => {
      if (key.startsWith('.')) {
        payload[key.slice(1)] = payload[key]
        delete payload[key]
      }
    })

    API.put('/edit-event', payload)
      .then((res) => {
        if (res.status === 200) {
          originalEventRef.current = cloneData(currentEventData)
          changedFieldsRef.current.clear()
          setChangesPresent(false)
        }
      })
      .catch((e) => {
        console.log(e)
        setToast({
          message: 'Unable to save changes',
          severity: 'error',
        })
      })
  }

  const eventMetaContextValue = useMemo(
    () => ({
      eventMeta,
      setEventMeta: updateTrackedState('eventMeta', setEventMeta),
      loadEvent,
      setEditMode,
      handleSave,
    }),
    [eventMeta, invoice, riskAssessment, opsPlan, medPlan, postEventAnalysis],
  )

  const opsPlanContextValue = useMemo(
    () => ({ opsPlan, setOpsPlan: updateTrackedState('opsPlan', setOpsPlan) }),
    [opsPlan],
  )
  const medPlanContextValue = useMemo(
    () => ({ medPlan, setMedPlan: updateTrackedState('medPlan', setMedPlan) }),
    [medPlan],
  )
  const riskAssessmentContextValue = useMemo(
    () => ({
      riskAssessment,
      setRiskAssessment: updateTrackedState(
        'riskAssessment',
        setRiskAssessment,
      ),
    }),
    [riskAssessment],
  )
  const postEventAnalysisContextValue = useMemo(
    () => ({
      postEventAnalysis,
      setPostEventAnalysis: updateTrackedState(
        'postEventAnalysis',
        setPostEventAnalysis,
      ),
    }),
    [postEventAnalysis],
  )
  const changesPresentContextValue = useMemo(
    () => ({ changesPresent, setChangesPresent }),
    [changesPresent],
  )
  const shiftsContextValue = useMemo(
    () => ({ shifts, expandedDays, setExpandedDays }),
    [shifts],
  )
  const plannerCommentsContextValue = useMemo(
    () => ({ plannerComments }),
    [plannerComments],
  )

  useEffect(() => {
    if (eventMeta?.title) {
      setPageTitle(eventMeta.title)
    }
  }, [eventMeta?.title, setPageTitle])

  return (
    <Box>
      <Box
        sx={{
          minHeight: '100%',
          bgcolor: '#f6f8fb',
          mx: { xs: -1, md: -2 },
          px: { xs: 1, md: 2 },
          py: 1,
        }}
      >
        <Stack gap={2}>
          {Object.keys(eventMeta).length === 0 && !loading ? (
            <Alert severity="error">No Matching Event</Alert>
          ) : loading ? (
            <LinearProgress />
          ) : (
            <Grid container spacing={2}>
              <ChangePresentContext.Provider value={changesPresentContextValue}>
                <EventMetaContext.Provider value={eventMetaContextValue}>
                  <ShiftContext.Provider value={shiftsContextValue}>
                    <TopPanel />
                  </ShiftContext.Provider>
                </EventMetaContext.Provider>
                <EventMetaContext.Provider value={eventMetaContextValue}>
                  <OpsPlanContext.Provider value={opsPlanContextValue}>
                    <ShiftContext.Provider value={shiftsContextValue}>
                      <CentreTabs
                        expandedDays={expandedDays}
                        setExpandedDays={setExpandedDays}
                      />
                    </ShiftContext.Provider>
                  </OpsPlanContext.Provider>
                </EventMetaContext.Provider>
                <Grid
                  item
                  xs={12}
                  lg={4}
                  flex={1}
                  gap={2}
                  display="flex"
                  flexDirection="column"
                >
                  <EventMetaContext.Provider value={eventMetaContextValue}>
                    <OpsPlanContext.Provider value={opsPlanContextValue}>
                      <MedPlanContext.Provider value={medPlanContextValue}>
                        <RiskAssessmentContext.Provider
                          value={riskAssessmentContextValue}
                        >
                          <DocumentGenerator />
                        </RiskAssessmentContext.Provider>
                      </MedPlanContext.Provider>
                    </OpsPlanContext.Provider>
                  </EventMetaContext.Provider>

                  <EventMetaContext.Provider value={eventMetaContextValue}>
                    <ShiftContext.Provider value={shiftsContextValue}>
                      <InvoicePanel />
                    </ShiftContext.Provider>
                  </EventMetaContext.Provider>
                </Grid>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={12} lg={6} display='flex'>
                    <EventMetaContext.Provider value={eventMetaContextValue}>
                      <TasksPane />
                    </EventMetaContext.Provider>
                  </Grid>
                  <Grid item xs={12} lg={6} display='flex'>
                    <EventMetaContext.Provider value={eventMetaContextValue}>
                      <PlannerCommentsContext.Provider
                        value={plannerCommentsContextValue}
                      >
                        <InternalNotes />
                      </PlannerCommentsContext.Provider>
                    </EventMetaContext.Provider>
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={12} lg={4}>
                    <EventMetaContext.Provider value={eventMetaContextValue}>
                      <DocumentsPanel />
                    </EventMetaContext.Provider>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <EventMetaContext.Provider value={eventMetaContextValue}>
                      <ParticipantsPanel />
                    </EventMetaContext.Provider>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <EventMetaContext.Provider value={eventMetaContextValue}>
                      <ClientFeedbackPanel />
                    </EventMetaContext.Provider>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      boxShadow: '0 12px 34px rgba(15, 23, 42, 0.06)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: { xs: 2, md: 3 },
                        py: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        gap={1.5}
                      >
                        <Stack gap={0.5}>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            Planning workspace
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Work through the event plan in focused stages.
                          </Typography>
                        </Stack>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={
                              eventMeta.EMPApproved
                                ? 'EMP approved'
                                : 'EMP draft'
                            }
                            color={
                              eventMeta.EMPApproved ? 'success' : 'warning'
                            }
                          />
                          {changesPresent && (
                            <Chip
                              size="small"
                              label="Unsaved changes"
                              color="info"
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        px: { xs: 1, md: 2 },
                        bgcolor: '#fbfcfe',
                        borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                      }}
                    >
                      <Tabs
                        value={selectedTab}
                        onChange={(e, value) => setSelectedTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          minHeight: 54,
                          '& .MuiTab-root': {
                            minHeight: 54,
                            textTransform: 'none',
                            fontWeight: 700,
                          },
                        }}
                      >
                        <Tab
                          value="intelligence"
                          label="Planning Intelligence"
                        />
                        <Tab value="ops" label="Operational plan" />
                        <Tab value="clinical" label="Clinical & EMP" />
                        <Tab value="risk" label="Risk assessment" />
                        <Tab value="compliance" label="CQC Exposure" />
                        <Tab value="pea" label="Post-event" />
                        <Tab value="costs" label="Costs" />
                      </Tabs>
                    </Box>
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      {selectedTab === 'intelligence' && (
                        <PlanningIntelligence
                          eventID={eventMeta._id}
                          onNavigateToTab={setSelectedTab}
                        />
                      )}
                      {selectedTab === 'ops' && (
                        <EventMetaContext.Provider
                          value={eventMetaContextValue}
                        >
                          <OpsPlanContext.Provider value={opsPlanContextValue}>
                            <Stack gap={3}>
                              <VehiclesInvolved />
                              <OpsPlan embedded />
                            </Stack>
                          </OpsPlanContext.Provider>
                        </EventMetaContext.Provider>
                      )}
                      {selectedTab === 'clinical' && (
                        <EventMetaContext.Provider
                          value={eventMetaContextValue}
                        >
                          <MedPlanContext.Provider value={medPlanContextValue}>
                            <MedPlan embedded />
                          </MedPlanContext.Provider>
                        </EventMetaContext.Provider>
                      )}
                      {selectedTab === 'risk' && (
                        <EventMetaContext.Provider
                          value={eventMetaContextValue}
                        >
                          <RiskAssessmentContext.Provider
                            value={riskAssessmentContextValue}
                          >
                            <RiskAssessment embedded />
                          </RiskAssessmentContext.Provider>
                        </EventMetaContext.Provider>
                      )}
                      {selectedTab === 'compliance' && (
                        <ExemptionWizard eventID={eventMeta._id} embedded />
                      )}
                      {selectedTab === 'pea' && (
                        <EventMetaContext.Provider
                          value={eventMetaContextValue}
                        >
                          <PostEventAnalysisContext.Provider
                            value={postEventAnalysisContextValue}
                          >
                            <ShiftContext.Provider value={shiftsContextValue}>
                              <PEA embedded />
                            </ShiftContext.Provider>
                          </PostEventAnalysisContext.Provider>
                        </EventMetaContext.Provider>
                      )}
                      {selectedTab === 'costs' && (
                        <EventMetaContext.Provider
                          value={eventMetaContextValue}
                        >
                          <EventCosts />
                        </EventMetaContext.Provider>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </ChangePresentContext.Provider>
            </Grid>
          )}
          {changesPresent && (
            <Slide
              direction="up"
              in={changesPresent}
              mountOnEnter
              unmountOnExit
            >
              <Stack
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'rgba(15, 23, 42, 0.96)',
                  color: 'white',
                  padding: 1.5,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.22)',
                }}
                direction={{ xs: 'column', lg: 'row' }}
                component={Paper}
                justifyContent="space-between"
                alignItems="center"
                zIndex={100}
                rowGap={1}
              >
                <Typography
                  fontFamily="poppins"
                  color="inherit"
                  textAlign="center"
                  fontSize="1rem"
                >
                  Unsaved changes
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button
                    onClick={() => {
                      loadEvent()
                      setChangesPresent(false)
                      changedFieldsRef.current.clear()
                    }}
                    variant="contained"
                    color="error"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    // sx={{color: 'white', borderColor: 'white'}}
                    color="success"
                    startIcon={<Save />}
                  >
                    Save Changes
                  </Button>
                </Stack>
              </Stack>
            </Slide>
          )}
          <BigModal open={editMode} onClose={() => setEditMode(false)}>
            <AddNewEvent
              eventMeta={eventMeta}
              opsPlanMeta={opsPlan}
              shifts={shifts}
              customCallbackFunction={() => {
                loadEvent()
                setEditMode(false)
              }}
            />
          </BigModal>
        </Stack>
      </Box>
    </Box>
  )
}

export default SingleEvent
